import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { LinearProgress } from "@mui/material";
// 2026-05-10: Toaster component'ı App tree'sine mount edilmemişti → tüm
// toast.error/success çağrıları sessiz kalıyordu (kullanıcı hata mesajı görmüyor).
import { Toaster } from "react-hot-toast";
// Faz BO (2026-05-10): eski Layout.js → yeni AppShell (sidebar refaktörü)
import AppShell from "./components/ui/AppShell";
import { clearAuth } from "./utils/authStorage";

// Login + ForgotPassword + ResetPassword eager: auth sayfalari ilk paint kritik.
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

// =============================================================================
// Faz Performance (2026-05-13): React.lazy code splitting — her sayfa kendi
// chunk'ında, sadece o sayfaya gidildiğinde indiriliyor. Bundan önce tek main.js
// 578 KB gzip idi; lazy split ile dashboard ilk yüklendiğinde sadece o page
// chunk'ı geliyor, geri kalan sayfalar ihtiyaç anında. Suspense fallback'i
// LinearProgress ile route geçişlerinde belli oluyor.
// =============================================================================
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Products = lazy(() => import("./pages/Products"));
const Categories = lazy(() => import("./pages/Categories"));
const Orders = lazy(() => import("./pages/Orders"));
const Users = lazy(() => import("./pages/Users"));
const PendingUsers = lazy(() => import("./pages/PendingUsers"));
const Suppliers = lazy(() => import("./pages/Suppliers"));
const EmailCampaign = lazy(() => import("./pages/EmailCampaign"));
const PazarAnalizi = lazy(() => import("./pages/PazarAnalizi"));
const StockManagement = lazy(() => import("./pages/StockManagement"));
const B2BPricing = lazy(() => import("./pages/B2BPricing"));
const PushCampaigns = lazy(() => import("./pages/PushCampaigns"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const Reports = lazy(() => import("./pages/Reports"));
const VatReport = lazy(() => import("./pages/VatReport"));
const Quotes = lazy(() => import("./pages/Quotes"));
const CariHesap = lazy(() => import("./pages/CariHesap"));
const ImportExport = lazy(() => import("./pages/Import"));
const Entegrasyonlar = lazy(() => import("./pages/Entegrasyonlar"));
const AuditLog = lazy(() => import("./pages/AuditLog"));
const AbandonedCart = lazy(() => import("./pages/AbandonedCart"));
const WelcomeEmail = lazy(() => import("./pages/WelcomeEmail"));
const DashboardModern = lazy(() => import("./pages/DashboardModern"));
const Customer360 = lazy(() => import("./pages/Customer360"));
const SilentCustomers = lazy(() => import("./pages/SilentCustomers"));
const MarketingHub = lazy(() => import("./pages/MarketingHub"));
const Settings = lazy(() => import("./pages/Settings"));
// Faz E.8 (2026-05-16): BH fatura retry kuyrugu
const BhRetryQueue = lazy(() => import("./pages/BhRetryQueue"));
const NpsDashboard = lazy(() => import("./pages/NpsDashboard"));
const Coupons = lazy(() => import("./pages/Coupons"));
const RfmAnalysis = lazy(() => import("./pages/RfmAnalysis"));
const CrossSell = lazy(() => import("./pages/CrossSell"));
const SalesFunnel = lazy(() => import("./pages/SalesFunnel"));
const ActionCenter = lazy(() => import("./pages/ActionCenter"));
const Forecast = lazy(() => import("./pages/Forecast"));
const SiteTraffic = lazy(() => import("./pages/SiteTraffic"));
const CohortRetention = lazy(() => import("./pages/CohortRetention"));
const CustomerJourney = lazy(() => import("./pages/CustomerJourney"));
const EmailPerformance = lazy(() => import("./pages/EmailPerformance"));
const SalesPattern = lazy(() => import("./pages/SalesPattern"));
const Cockpit = lazy(() => import("./pages/Cockpit"));
const CategorySales = lazy(() => import("./pages/CategorySales"));
const SmartRecommendations = lazy(() => import("./pages/SmartRecommendations"));
const GeoAnalytics = lazy(() => import("./pages/GeoAnalytics"));
const Product360 = lazy(() => import("./pages/Product360"));
const DripBuilder = lazy(() => import("./pages/DripBuilder"));
const SystemHealth = lazy(() => import("./pages/SystemHealth"));
const MonthlyGoals = lazy(() => import("./pages/MonthlyGoals"));
const WhatsAppPanel = lazy(() => import("./pages/WhatsAppPanel"));
const Notes = lazy(() => import("./pages/Notes"));
const Subscriptions = lazy(() => import("./pages/Subscriptions"));
const ReviewsModeration = lazy(() => import("./pages/ReviewsModeration"));
const Returns = lazy(() => import("./pages/Returns"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const BulkCustomerImport = lazy(() => import("./pages/BulkCustomerImport"));
const BulkProductImport = lazy(() => import("./pages/BulkProductImport"));
const StockAdjustments = lazy(() => import("./pages/StockAdjustments"));

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 dakika
const WARN_BEFORE_MS  =  2 * 60 * 1000; //  2 dakika öncesinde uyar

function IdleGuard({ children }) {
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const warnRef  = useRef(null);
  const [warning, setWarning] = useState(false);

  const logout = () => {
    // clearAuth(): sadece auth key'lerini siler; theme + sidebar tercihi korunur.
    clearAuth();
    setWarning(false);
    navigate("/login");
  };

  const resetTimer = () => {
    clearTimeout(timerRef.current);
    clearTimeout(warnRef.current);
    setWarning(false);
    warnRef.current  = setTimeout(() => setWarning(true),  IDLE_TIMEOUT_MS - WARN_BEFORE_MS);
    timerRef.current = setTimeout(logout, IDLE_TIMEOUT_MS);
  };

  useEffect(() => {
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(timerRef.current);
      clearTimeout(warnRef.current);
    };
  }, []); // eslint-disable-line

  return (
    <>
      {warning && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
          background: "#f57c00", color: "#fff", textAlign: "center",
          padding: "10px 16px", fontWeight: 600, fontSize: 14,
          display: "flex", justifyContent: "center", alignItems: "center", gap: 16
        }}>
          ⚠️ Hareketsizlik nedeniyle 2 dakika içinde oturum kapatılacak.
          <button onClick={resetTimer} style={{
            background: "#fff", color: "#f57c00", border: "none",
            borderRadius: 6, padding: "4px 14px", cursor: "pointer", fontWeight: 700
          }}>
            Devam Et
          </button>
        </div>
      )}
      {children}
    </>
  );
}

const PrivateRoute = ({ children }) => {
  if (!localStorage.getItem("token")) return <Navigate to="/login" />;
  return <IdleGuard>{children}</IdleGuard>;
};

export default function App() {
  return (
    <BrowserRouter>
      {/* Global toast container — tüm sayfaların toast.success/error çağrıları buradan render olur */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: { fontSize: 14, fontWeight: 500 },
          success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
          error: { duration: 7000, iconTheme: { primary: '#ef4444', secondary: '#fff' } }
        }}
      />
      <Suspense fallback={<LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1500 }} />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/" element={<PrivateRoute><AppShell /></PrivateRoute>}>
          <Route index element={<Navigate to="/dashboard" />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="products" element={<Products />} />
          <Route path="categories" element={<Categories />} />
          <Route path="orders" element={<Orders />} />
          <Route path="users" element={<Users />} />
          <Route path="pending" element={<PendingUsers />} />
          <Route path="suppliers" element={<Suppliers />} />
          <Route path="email-campaign" element={<EmailCampaign />} />
          <Route path="pazar-analizi" element={<PazarAnalizi />} />
          {/* Faz F: Modern Dashboard + Müşteri 360 */}
          <Route path="dashboard-v2" element={<DashboardModern />} />
          <Route path="customer/:id" element={<Customer360 />} />
          <Route path="silent-customers" element={<SilentCustomers />} />
          {/* Faz G: Marketing Hub + Settings */}
          <Route path="marketing" element={<MarketingHub />} />
          <Route path="settings" element={<Settings />} />
          {/* Faz E.8: BH fatura retry kuyrugu */}
          <Route path="bh-retry-queue" element={<BhRetryQueue />} />
          {/* Faz I: NPS Dashboard + Kupon Yönetimi */}
          <Route path="nps" element={<NpsDashboard />} />
          <Route path="coupons" element={<Coupons />} />
          {/* Faz J: RFM Müşteri Analizi */}
          <Route path="rfm" element={<RfmAnalysis />} />
          {/* Faz K: Cross-sell Intelligence */}
          <Route path="cross-sell" element={<CrossSell />} />
          {/* Faz L: Sales Funnel */}
          <Route path="funnel" element={<SalesFunnel />} />
          {/* Faz M: Action Center */}
          <Route path="action-center" element={<ActionCenter />} />
          {/* Faz N: Forecast */}
          <Route path="forecast" element={<Forecast />} />
          {/* Faz P: Site Trafiği */}
          <Route path="site-traffic" element={<SiteTraffic />} />
          {/* Faz R: Cohort & Retention */}
          <Route path="cohort" element={<CohortRetention />} />
          {/* Faz T: Customer Journey Map */}
          <Route path="journey" element={<CustomerJourney />} />
          {/* Faz U: Email Campaign Performance */}
          <Route path="email-performance" element={<EmailPerformance />} />
          {/* Faz V: Sales Pattern Heatmap */}
          <Route path="sales-pattern" element={<SalesPattern />} />
          {/* Faz X: Mobile Cockpit */}
          <Route path="cockpit" element={<Cockpit />} />
          {/* Faz Y: Category & Brand Sales */}
          <Route path="category-sales" element={<CategorySales />} />
          {/* Faz Z: Smart Recommendations */}
          <Route path="recommendations" element={<SmartRecommendations />} />
          {/* Faz AA: Geographic Analytics */}
          <Route path="geo" element={<GeoAnalytics />} />
          {/* Faz AB: Product 360 — listeden ürün seçimi veya direkt ID */}
          <Route path="product360" element={<Product360 />} />
          <Route path="product360/:id" element={<Product360 />} />
          {/* Faz AC: Drip Campaign Builder */}
          <Route path="drip-builder" element={<DripBuilder />} />
          {/* Faz AD: System Health + Backup */}
          <Route path="system-health" element={<SystemHealth />} />
          {/* Faz AF: Aylık Hedef + Progress Tracker */}
          <Route path="monthly-goals" element={<MonthlyGoals />} />
          {/* Faz AK: WhatsApp Panel */}
          <Route path="whatsapp" element={<WhatsAppPanel />} />
          {/* Faz AM: Owner Notes / Sticky Memos */}
          <Route path="notes" element={<Notes />} />
          {/* Faz AN: Subscription / Repeat Order */}
          <Route path="subscriptions" element={<Subscriptions />} />
          {/* Faz AO: Reviews Moderation */}
          <Route path="reviews" element={<ReviewsModeration />} />
          {/* Faz AQ: Returns Moderation */}
          <Route path="returns" element={<Returns />} />
          {/* Faz AT: Admin User Management + RBAC */}
          <Route path="admin-users" element={<AdminUsers />} />
          {/* Faz AU: Bulk Customer Import */}
          <Route path="bulk-import" element={<BulkCustomerImport />} />
          {/* Faz AV: Bulk Product Import */}
          <Route path="bulk-product-import" element={<BulkProductImport />} />
          {/* Faz AW: Stock Adjustment */}
          <Route path="stock-adjustments" element={<StockAdjustments />} />
          <Route path="stock" element={<StockManagement />} />
          <Route path="b2b-pricing" element={<B2BPricing />} />
          <Route path="push-campaigns" element={<PushCampaigns />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="reports" element={<Reports />} />
          <Route path="vat-report" element={<VatReport />} />
          <Route path="quotes" element={<Quotes />} />
          <Route path="cari-hesap" element={<CariHesap />} />
          <Route path="import-export" element={<ImportExport />} />
          <Route path="integrations" element={<Entegrasyonlar />} />
          {/* Faz 12a route — sidebar HistoryIcon ile baglandi */}
          <Route path="audit-log" element={<AuditLog />} />
          {/* Faz 14b route — Sepeti Terk Hatirlatma raporu (sidebar RemoveShoppingCartIcon) */}
          <Route path="abandoned-cart" element={<AbandonedCart />} />
          {/* Faz 14a route — Welcome Email Otomasyonu raporu */}
          <Route path="welcome-email" element={<WelcomeEmail />} />
        </Route>
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}